import { useMemo, useState } from "react";
import { View } from "@common/types/views/views";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Loader } from "@design-system/Loader/Loader";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { usePreviewCloseTemplateQuery } from "@api/closeTemplate.api";
import { useOrganizationCloseStages } from "@api/organizationCloseStage.api";
import { useCloseTemplateTaskTemplates } from "@api/taskTemplate.api";
import {
    useWorkspaceUserCache,
    WORKSPACE_USER_CACHE_KEYS,
} from "@hooks/useWorkspaceUserCache";
import type { SelectedEntityEntry } from "./ApplyCloseTemplateStepReview";
import {
    TASK_TYPE_LABEL,
    stageKey,
} from "./closeTemplateTasksDrawer.constants";
import { getWorkspaceMemberDisplayName } from "./applyCloseTemplateOrganizationMembers";
import { useTemplateApplication } from "./useTemplateApplication";
import {
    CLIENT_HIDDEN_ROLES,
    TaskPreviewRow,
    TaskPreviewViewSchema,
    createDefaultTaskPreviewView,
    taskPreviewViewConfig,
} from "./taskPreviewViewConfig";
import { CloseTemplateOverlapWarning } from "./CloseTemplateOverlapWarning";
import { resolvePreviewTaskLinks } from "./taskPreviewLinks";

interface Props {
    closeTemplateId: string;
    selectedEntity: SelectedEntityEntry;
    viewingAs: "organization" | "client";
}

// Every reviewed row (both the one-off apply variant, where these are
// per-entity editable columns, and the schedule variant, where dates are
// filled in from the first previewed occurrence and assignees come from
// the entity row — see ApplyCloseTemplateWizardBody's `reviewEntries`)
// already carries its own workStartDate/workDeliveryDate, so the preview
// body is built entirely from the row.
function buildPreviewBody(selectedEntity: SelectedEntityEntry) {
    const row = selectedEntity.row;
    if (
        !row.startDate ||
        !row.endDate ||
        !row.workStartDate ||
        !row.workDeliveryDate
    ) {
        return null;
    }
    return {
        workspaceId: row.workspaceId,
        entityIds: [Number(selectedEntity.entityIdStr)],
        startDate: row.startDate,
        endDate: row.endDate,
        workStartDate: row.workStartDate,
        workDeliveryDate: row.workDeliveryDate,
        preparerId: row.preparerId,
        reviewerId: row.reviewerId,
        managerId: row.managerId ?? null,
        clientId: row.clientId ?? null,
    };
}

export const ApplyCloseTemplateEntityPreview = ({
    closeTemplateId,
    selectedEntity,
    viewingAs,
}: Props) => {
    const [search, setSearch] = useState("");

    const [cachedView, setCachedView] = useWorkspaceUserCache<View>(
        WORKSPACE_USER_CACHE_KEYS.CLOSE_TEMPLATE_APPLY_PREVIEW_VIEW_CACHE_KEY,
        TaskPreviewViewSchema,
    );
    const defaultView = useMemo(() => createDefaultTaskPreviewView(), []);
    const [view, setView] = useState<View>(cachedView ?? defaultView);

    const { getMemberLabel, workspaceMembersMap, members } =
        useTemplateApplication();
    const { data: stages } = useOrganizationCloseStages();
    const { data: taskTemplates } =
        useCloseTemplateTaskTemplates(closeTemplateId);

    const hiddenTemplateTaskIds = useMemo<ReadonlySet<string>>(() => {
        if (viewingAs !== "client" || !taskTemplates) {
            return new Set();
        }
        return new Set(
            taskTemplates
                .filter(
                    (t) =>
                        t.assigneeRole != null &&
                        CLIENT_HIDDEN_ROLES.has(t.assigneeRole),
                )
                .map((t) => t.id),
        );
    }, [viewingAs, taskTemplates]);

    const previewBody = useMemo(
        () => buildPreviewBody(selectedEntity),
        [selectedEntity],
    );

    const {
        data: previewData,
        isLoading,
        isError,
    } = usePreviewCloseTemplateQuery(closeTemplateId, previewBody);

    const taskPreviewRows = useMemo<TaskPreviewRow[]>(() => {
        const resolveLabel = (userId: number | null) =>
            getWorkspaceMemberDisplayName({
                workspaceMembersMap,
                workspaceId: selectedEntity.row.workspaceId,
                userId,
                fallback: getMemberLabel,
            });

        const resolveUser = (
            userId: number | null,
        ): TaskPreviewRow["assigneeUser"] => {
            if (userId == null) {
                return null;
            }
            const workspaceMembers =
                workspaceMembersMap[selectedEntity.row.workspaceId] ?? [];
            const workspaceMember =
                workspaceMembers.find((m) => m.user.id === userId) ?? null;
            if (workspaceMember) {
                return workspaceMember.user;
            }
            const orgMember = members.find((m) => m.user.id === userId) ?? null;
            return orgMember?.user ?? null;
        };

        const sortedStages = [...(stages ?? [])].sort(
            (a, b) => a.order - b.order,
        );
        const knownStageIds = new Set(sortedStages.map((stage) => stage.id));
        const visibleTasks = (previewData?.tasks ?? []).filter(
            (task) => !hiddenTemplateTaskIds.has(task.templateTaskId),
        );

        const linksByTaskId = resolvePreviewTaskLinks(visibleTasks);

        return [...sortedStages, null]
            .map((stage) => {
                const key = stageKey(stage?.id ?? null);
                const children = visibleTasks
                    .filter((task) =>
                        stage
                            ? task.closeStageId === stage.id
                            : task.closeStageId === null ||
                              !knownStageIds.has(task.closeStageId ?? ""),
                    )
                    .map<TaskPreviewRow>((task) => {
                        const isSkipped = Boolean(task.skipReason);
                        const resolvedAssignee =
                            task.assigneeId != null
                                ? resolveLabel(task.assigneeId)
                                : "No assignee";
                        const assignee = isSkipped ? "" : resolvedAssignee;
                        const resolvedTaskType =
                            task.type != null
                                ? (TASK_TYPE_LABEL[task.type] ??
                                  String(task.type))
                                : "-";
                        const taskType = isSkipped ? "" : resolvedTaskType;
                        return {
                            id: task.id,
                            name: task.title,
                            assignee,
                            assigneeUser: isSkipped
                                ? null
                                : resolveUser(task.assigneeId),
                            taskType,
                            closeStage: isSkipped
                                ? ""
                                : (stage?.name ?? "No stage"),
                            dueDate: isSkipped ? "" : (task.dueDate ?? "-"),
                            blockedBy: isSkipped
                                ? ""
                                : (linksByTaskId.get(task.id)?.blockedBy ?? ""),
                            relatedTo: isSkipped
                                ? ""
                                : (linksByTaskId.get(task.id)?.relatedTo ?? ""),
                            skipReason: task.skipReason ?? null,
                        };
                    });

                return {
                    id: key,
                    name: stage?.name ?? "No stage",
                    assignee: "",
                    assigneeUser: null,
                    taskType: "",
                    closeStage: "",
                    dueDate: "",
                    blockedBy: "",
                    relatedTo: "",
                    isGroupRow: true,
                    children,
                };
            })
            .filter((group) => (group.children?.length ?? 0) > 0);
    }, [
        getMemberLabel,
        hiddenTemplateTaskIds,
        members,
        previewData?.tasks,
        selectedEntity.row.workspaceId,
        stages,
        workspaceMembersMap,
    ]);

    const filteredTaskPreviewRows = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();
        if (!normalizedSearch) {
            return taskPreviewRows;
        }

        return taskPreviewRows
            .map<TaskPreviewRow | null>((groupRow) => {
                if (!groupRow.isGroupRow) {
                    return groupRow;
                }

                const matchingChildren =
                    groupRow.children?.filter((task) => {
                        const searchable = [
                            task.name,
                            task.assignee,
                            task.taskType,
                            task.closeStage,
                            task.dueDate,
                            task.blockedBy,
                            task.relatedTo,
                        ]
                            .join(" ")
                            .toLowerCase();

                        return searchable.includes(normalizedSearch);
                    }) ?? [];

                if (matchingChildren.length === 0) {
                    return null;
                }

                return {
                    ...groupRow,
                    children: matchingChildren,
                };
            })
            .filter((row): row is TaskPreviewRow => row !== null);
    }, [search, taskPreviewRows]);

    const handleViewChange = (newView: View) => {
        setView(newView);
        setCachedView(newView);
    };

    if (!previewBody) {
        return (
            <p className="text-muted small">
                Fill in start date, end date, work start date, and work end date
                to see a preview.
            </p>
        );
    }

    if (isLoading) {
        return <Loader />;
    }

    if (isError) {
        return (
            <p className="text-muted small">
                Failed to load preview. Please try again.
            </p>
        );
    }

    return (
        <div>
            {previewData?.overlappingCloses &&
                previewData.overlappingCloses.length > 0 && (
                    <CloseTemplateOverlapWarning
                        overlappingCloses={previewData.overlappingCloses}
                    />
                )}
            <TablePageHeader
                search={search}
                onSearchChange={setSearch}
                searchPlaceholder="Search tasks..."
                searchKeyboardShortcuts={false}
                isFullPage
            >
                <ColumnSettings
                    view={view}
                    viewConfig={taskPreviewViewConfig}
                    defaultView={defaultView}
                    onViewChange={handleViewChange}
                />
            </TablePageHeader>
            <ViewTable
                data={filteredTaskPreviewRows}
                view={view}
                config={taskPreviewViewConfig}
                onViewChange={handleViewChange}
                emptyView={() => <EmptyState header="No tasks yet" />}
                enableColumnResizing
                autoExpandOnMount
                outerBorder={false}
                nestingIndentPx={12}
                fixedTopOffsetPx
            />
        </div>
    );
};
